// Generated from scripts/quick-view.ts. Rebuild with pnpm scripts:build.
import { Component } from '@theme/component';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';
import { getIOSVersion, isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';
// Defines `product-component`, the element that holds the dialog's content and publishes its view event.
import '@theme/view-event-elements';
/**
 * Quick view: the product page's product section in a dialog, opened from a product card, a hotspot or a
 * recommendation row without leaving the page.
 *
 * Two elements, as in the inherited module they replace. `quick-add-component` is the trigger inside a card (the
 * Add control, or the Choose control for a product with options). `quick-add-dialog` is the one dialog in the page
 * layout. A trigger fetches the product page, takes its `[data-product-grid-content]` (the gallery and the details
 * with the variant picker, the price and the buy buttons) and hands it to the dialog, which shows it.
 *
 * Timing. A pointer resting on a card for 150 ms, or focus moving into it, starts the fetch; leaving before the
 * shopper commits aborts it. A press on Choose starts it at once. The fetched section is cached per product URL until
 * the cart changes (stock and "in cart" notes depend on it). The dialog opens in the task of the press (iOS raises
 * the keyboard and honors focus only there), with the product's title already in its heading and a placeholder in
 * place of the section when the fetch has not finished, so the loading state is on screen in the same frame. A
 * cached section is in the dialog before it opens.
 *
 * Lifecycle. Modules that the fetched markup declares do not run when it is inserted, so the module URLs it names
 * (theme assets only) are imported once per page, from the moment the fetch lands, and the elements upgrade when
 * they connect. Closing empties the dialog, so every element in it disconnects and tears itself down.
 *
 * Accessibility. The dialog is labelled by the product's title: the heading in the placeholder first, then the
 * heading of the fetched section, which takes over the id and, when the placeholder's heading had focus, the focus.
 * Focus lands on that heading when the dialog opens, stays in the dialog (a native modal) and returns to the control
 * that opened it. A fetch that fails leaves the heading, a sentence and a link to the product page.
 *
 * Contract kept from the inherited module: the `quick-add-component` and `quick-add-dialog` elements, the
 * `productPageUrl` getter, `fetchProductPage` and `handleClick`, the `stay-visible` attribute on the trigger while
 * the dialog is open, the choose and add flip of a single-option card when a swatch is chosen, and the dialog's
 * closing on a successful add. The dialog publishes `quick-view:open` (see `QuickViewOpenEvent`).
 */
/** How long a pointer or focus rests on a card before its product page starts loading. */
const INTENT_DELAY = 150;
/** The id of the heading that names the dialog. It sits on the placeholder's heading, then on the section's. */
const TITLE_ID = 'quick-add-dialog-title';
/**
 * Published on the dialog, and so on the document, when a quick view opens. It carries the product and the surface
 * the shopper opened it from. It holds nothing about the shopper and calls no third party: an analytics bridge
 * listens for it.
 */
export class QuickViewOpenEvent extends CustomEvent {
    static eventName = 'quick-view:open';
    constructor(detail) {
        super(QuickViewOpenEvent.eventName, { bubbles: true, composed: true, detail });
    }
}
/** A product page section fetched for the page's life, by product page URL; a cart change empties it. */
const contentCache = new Map();
/** Imports in flight or done, by URL, so a module is requested once per page. */
const loadedModules = new Map();
/** The directory this module was loaded from: the only place the fetched markup may name modules in. */
const THEME_ASSETS = new URL('.', import.meta.url).href;
document.addEventListener(StandardEvents.cartLinesUpdate, () => contentCache.clear());
function isAbort(error) {
    return error instanceof Error && error.name === 'AbortError';
}
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
function savesData() {
    const { connection } = navigator;
    return connection?.saveData === true;
}
/** An address on this site, or undefined: a link the dialog writes never leaves the storefront. */
function sameOriginHref(value) {
    try {
        const url = new URL(value, window.location.href);
        return url.origin === window.location.origin ? url.href : undefined;
    }
    catch {
        return undefined;
    }
}
/** The payload of the standard product view event, saying it happened in a dialog unless it names another place. */
function withDialogContext(payload) {
    try {
        const parsed = JSON.parse(payload);
        if (isRecord(parsed))
            return JSON.stringify({ ...parsed, context: parsed.context ?? 'dialog' });
    }
    catch {
        // The view event element reports a payload it cannot read.
    }
    return payload;
}
function isProductCard(element) {
    return element instanceof HTMLElement && 'productPageUrl' in element && typeof element.productPageUrl === 'string';
}
function isViewEventElement(element) {
    return element instanceof HTMLElement && 'dispatchViewEvent' in element && typeof element.dispatchViewEvent === 'function';
}
/** A standard event that settles later (a variant selection, a cart update): its promise carries what Shopify answered. */
function hasPromise(event) {
    return 'promise' in event && event.promise instanceof Promise;
}
/** The theme's own `detail` of a settled standard event, when it has one. */
function detailOf(result) {
    return isRecord(result) && isRecord(result.detail) ? result.detail : undefined;
}
/** The theme's own module scripts that a fetched section declares, as absolute URLs. */
function moduleUrlsOf(section) {
    const urls = new Set();
    for (const script of section.querySelectorAll('script[type="module"][src]')) {
        const source = script.getAttribute('src');
        if (!source)
            continue;
        try {
            const url = new URL(source, window.location.href);
            if (url.pathname.endsWith('.js') && new URL('.', url).href === THEME_ASSETS)
                urls.add(url.href);
        }
        catch {
            // An address the browser cannot read names no module.
        }
    }
    return [...urls];
}
function loadModule(url) {
    let loading = loadedModules.get(url);
    if (!loading) {
        loading = import(url).then(() => undefined, (error) => {
            loadedModules.delete(url);
            console.warn('[quick-view] A product module did not load:', url, error);
        });
        loadedModules.set(url, loading);
    }
    return loading;
}
function loadModules(urls) {
    return Promise.all(urls.map(loadModule)).then(() => undefined);
}
async function fetchContent(url, signal) {
    const response = await fetch(url, { signal });
    if (!response.ok)
        throw new Error(`Failed to fetch the product page: HTTP ${response.status}`);
    const page = new DOMParser().parseFromString(await response.text(), 'text/html');
    const grid = page.querySelector('[data-product-grid-content]');
    if (!grid)
        throw new Error('The product page has no product section.');
    return { grid, moduleUrls: moduleUrlsOf(grid) };
}
/**
 * The trigger inside a product card, a recommendation row or a hotspot.
 *
 * It loads the product page when the shopper shows intent, opens the dialog on a press, and keeps the card's
 * Choose and Add controls in step with the swatch chosen on a single-option card.
 */
export class QuickAddComponent extends Component {
    #lifetime;
    #intentTimer = 0;
    #pending;
    #hovered = false;
    #focused = false;
    /** The address of the product page the dialog would show, including the variant chosen on the card. */
    get productPageUrl() {
        const card = this.closest('product-card');
        if (isProductCard(card))
            return card.productPageUrl;
        // A row of a list or a look's product has no card; it names its product's address itself.
        const own = this.dataset.productUrl;
        return (own ? sameOriginHref(own) : undefined) ?? '';
    }
    connectedCallback() {
        super.connectedCallback();
        this.#lifetime?.abort();
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        const { signal } = lifetime;
        // Intent is read from the card, or from the row of a list that marks itself `data-quick-view-host`.
        const card = this.closest('product-card');
        const host = this.closest('product-card, [data-quick-view-host]') ?? this;
        host.addEventListener('pointerenter', this.#onPointerEnter, { signal });
        host.addEventListener('pointerleave', this.#onPointerLeave, { signal });
        host.addEventListener('focusin', this.#onFocusIn, { signal });
        host.addEventListener('focusout', this.#onFocusOut, { signal });
        this.addEventListener('pointerdown', this.#onPointerDown, { signal });
        card?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = undefined;
        this.#hovered = false;
        this.#focused = false;
        this.#cancelIntent();
    }
    /* ----- intent ----- */
    get #opensDialog() {
        if (this.dataset.usesSellingPlans === 'true')
            return false;
        return this.querySelector('.quick-add__button--choose') !== null;
    }
    #onPointerEnter = (event) => {
        // A touch press is a click a moment later; the press itself starts the load.
        if (event.pointerType === 'touch')
            return;
        this.#hovered = true;
        this.#scheduleIntent();
    };
    #onPointerLeave = () => {
        this.#hovered = false;
        if (!this.#focused)
            this.#cancelIntent();
    };
    #onFocusIn = () => {
        this.#focused = true;
        this.#scheduleIntent();
    };
    #onFocusOut = (event) => {
        const host = event.currentTarget;
        if (host instanceof Node && event.relatedTarget instanceof Node && host.contains(event.relatedTarget))
            return;
        this.#focused = false;
        if (!this.#hovered)
            this.#cancelIntent();
    };
    #onPointerDown = (event) => {
        if (event.target instanceof Element && event.target.closest('.quick-add__button--choose'))
            this.preload();
    };
    #scheduleIntent() {
        window.clearTimeout(this.#intentTimer);
        this.#intentTimer = window.setTimeout(() => this.preload(), INTENT_DELAY);
    }
    /** Forgets a pending intent and aborts a load that no press has committed to. */
    #cancelIntent() {
        window.clearTimeout(this.#intentTimer);
        const pending = this.#pending;
        if (pending && !pending.claimed) {
            pending.controller.abort();
            this.#pending = undefined;
        }
    }
    /** Starts loading the product page for this trigger. A product page already loaded or in flight is not loaded again. */
    preload() {
        if (!this.#opensDialog || savesData())
            return;
        void this.fetchProductPage(this.productPageUrl);
    }
    /** Loads the product page again when the card is still under the pointer or focus, as after a swatch changed the variant. */
    refreshPreload() {
        if (this.#hovered || this.#focused)
            this.preload();
    }
    /** Loads a product page into the cache. It never rejects: a load that fails or is aborted costs nothing, and opening loads again. */
    async fetchProductPage(productPageUrl) {
        if (!productPageUrl)
            return;
        try {
            await this.#request(productPageUrl, false);
        }
        catch {
            // Opening the dialog reports a load that still fails by then.
        }
    }
    #request(url, claimed) {
        const cached = contentCache.get(url);
        if (cached)
            return Promise.resolve(cached);
        const current = this.#pending;
        if (current?.url === url) {
            if (claimed)
                current.claimed = true;
            return current.promise;
        }
        if (current && !current.claimed)
            current.controller.abort();
        const controller = new AbortController();
        const promise = fetchContent(url, controller.signal).then((content) => {
            contentCache.set(url, content);
            void loadModules(content.moduleUrls);
            return content;
        });
        const pending = { url, controller, promise, claimed };
        this.#pending = pending;
        const clear = () => {
            if (this.#pending === pending)
                this.#pending = undefined;
        };
        promise.then(clear, clear);
        return promise;
    }
    /* ----- the Choose and Add controls of a card ----- */
    /**
     * A single-option card shows Add for a swatch that can be bought and Choose otherwise, so the shopper never meets
     * a dead Add. Both controls are rendered only for that shape (`data-renders-both-buttons`); every other card has
     * the one it needs.
     */
    #onProductSelect = (event) => {
        if (this.dataset.usesSellingPlans === 'true' || this.dataset.rendersBothButtons !== 'true')
            return;
        let control = this.dataset.productOptionsCount === '1' ? 'add' : 'choose';
        const card = this.closest('product-card');
        if (control === 'add' && isProductCard(card) && card.variantPicker?.selectedOption?.dataset.optionAvailable === 'false') {
            control = 'choose';
        }
        this.setAttribute('data-quick-add-button', control);
    };
    /* ----- opening ----- */
    /** Opens the dialog for this product. The dialog is open when this returns; the section follows when it has loaded. */
    handleClick = (event) => {
        event.preventDefault();
        const url = this.productPageUrl;
        if (!url)
            return;
        // Subscription widgets only run on the product page, and a page without the dialog has nowhere to show one.
        const dialog = document.getElementById('quick-add-dialog');
        if (this.dataset.usesSellingPlans === 'true' || !(dialog instanceof QuickAddDialog)) {
            window.location.href = url;
            return;
        }
        const button = event.target instanceof Element ? event.target.closest('button') : null;
        const content = contentCache.get(url) ?? this.#request(url, true);
        const opened = dialog.present({
            trigger: button,
            title: this.dataset.productTitle ?? '',
            productId: this.dataset.productId ?? '',
            productUrl: url,
            context: this.#context,
            content,
        });
        if (!opened) {
            // A dialog that is already open shows nothing of this load; the cache keeps it if it lands.
            if (content instanceof Promise)
                content.catch(() => undefined);
            return;
        }
        // The control stays on screen while the dialog is open, though the pointer is no longer over the card.
        this.toggleAttribute('stay-visible', true);
        dialog.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), { once: true });
    };
    get #context() {
        const surface = this.closest('[data-quick-view-context]');
        return surface?.dataset.quickViewContext || 'card';
    }
}
if (!customElements.get('quick-add-component')) {
    customElements.define('quick-add-component', QuickAddComponent);
}
/**
 * The quick view dialog. Its content is a lifecycle transition: it holds the placeholder (and the error) while a
 * section loads, the section while the dialog is open, and nothing once it has closed.
 */
export class QuickAddDialog extends DialogComponent {
    #lifetime;
    /** Counts presentations and closings, so a section that arrives late for a closed or replaced dialog is dropped. */
    #generation = 0;
    connectedCallback() {
        super.connectedCallback();
        this.#lifetime?.abort();
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        const { signal } = lifetime;
        this.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, { signal });
        this.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
        this.addEventListener(DialogCloseEvent.eventName, this.#onClosed, { signal });
        // The phone and tablet layouts are different dialogs: crossing the breakpoint closes this one.
        mediaQueryLarge.addEventListener('change', this.#onBreakpoint, { signal });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = undefined;
    }
    get #dialog() {
        const { dialog } = this.refs;
        return dialog instanceof HTMLDialogElement ? dialog : null;
    }
    get #root() {
        return this.querySelector('[data-quick-view-content]');
    }
    get #loading() {
        return this.querySelector('[data-quick-view-loading]');
    }
    /** The heading of the placeholder: the one that exists before the section has loaded. */
    get #placeholderHeading() {
        return this.#loading?.querySelector('[data-quick-view-heading]') ?? null;
    }
    /**
     * Opens the dialog in the calling task, for a section that is already here or that will be.
     * @returns false when the dialog is already open.
     */
    present(options) {
        const dialog = this.#dialog;
        if (!dialog || dialog.open)
            return false;
        const generation = ++this.#generation;
        this.#reset();
        // A name for the dialog that outlives a section without a heading.
        dialog.setAttribute('aria-label', options.title);
        const { content } = options;
        if (content instanceof Promise)
            this.#showLoading(options.title);
        else
            this.#fill(content, options);
        // The dialog remembers the focused control to return to; Safari and Firefox do not focus a pressed button.
        options.trigger?.focus({ preventScroll: true });
        this.showDialog();
        this.querySelector(`#${TITLE_ID}`)?.focus({ preventScroll: true });
        this.dispatchEvent(new QuickViewOpenEvent({ productId: options.productId, productUrl: options.productUrl, context: options.context }));
        if (content instanceof Promise)
            void this.#settle(content, options, generation);
        else
            this.#dispatchView();
        return true;
    }
    async #settle(pending, options, generation) {
        let content;
        try {
            content = await pending;
        }
        catch {
            if (this.#isCurrent(generation))
                this.#showError(options);
            return;
        }
        if (!this.#isCurrent(generation))
            return;
        this.#fill(content, options);
        this.#dispatchView();
    }
    #isCurrent(generation) {
        return generation === this.#generation && this.#dialog?.open === true;
    }
    /* ----- the three things the dialog can hold ----- */
    #showLoading(title) {
        const loading = this.#loading;
        const heading = this.#placeholderHeading;
        if (!loading || !heading)
            return;
        heading.textContent = title;
        loading.dataset.state = 'loading';
        loading.hidden = false;
        this.#root?.setAttribute('aria-busy', 'true');
        this.#say(this.dataset.loadingText);
    }
    /** A section that could not be loaded: the heading stays, with a sentence and a link to the product page. */
    #showError(options) {
        const loading = this.#loading;
        if (!loading)
            return;
        loading.dataset.state = 'error';
        loading.hidden = false;
        this.#root?.removeAttribute('aria-busy');
        const link = loading.querySelector('a[data-quick-view-details]');
        const href = sameOriginHref(options.productUrl);
        if (link instanceof HTMLAnchorElement && href)
            link.href = href;
        this.#say(this.dataset.errorText);
    }
    /** Puts the fetched section in the dialog, replacing the placeholder. */
    #fill(content, options) {
        const root = this.#root;
        const loading = this.#loading;
        if (!root || !loading)
            return;
        const grid = document.importNode(content.grid, true);
        if (!(grid instanceof HTMLElement)) {
            this.#showError(options);
            return;
        }
        const linked = grid.querySelector('a[data-quick-view-details]')?.getAttribute('href');
        const href = (linked ? sameOriginHref(linked) : undefined) ?? sameOriginHref(options.productUrl) ?? options.productUrl;
        if (isMobileBreakpoint())
            this.#arrangeForPhone(grid, options.title, href);
        // The section's heading, found before the placeholder (which has one too) shares the dialog with it.
        const heading = grid.querySelector('[data-quick-view-heading]');
        const placeholderHeading = this.#placeholderHeading;
        const hadFocus = placeholderHeading !== null && document.activeElement === placeholderHeading;
        const payload = grid.getAttribute('view-event-payload');
        if (payload)
            root.setAttribute('view-event-payload', withDialogContext(payload));
        else
            root.removeAttribute('view-event-payload');
        root.replaceChildren(loading, ...grid.childNodes);
        loading.hidden = true;
        root.removeAttribute('aria-busy');
        this.#say('');
        // The dialog is named by its heading; a page title inside it is one level down from the page's own.
        for (const title of root.querySelectorAll('h1'))
            title.setAttribute('aria-level', '2');
        if (heading) {
            placeholderHeading?.removeAttribute('id');
            heading.id = TITLE_ID;
            heading.tabIndex = -1;
            if (hadFocus)
                heading.focus({ preventScroll: true });
        }
        // The modules were requested when the section landed; elements upgrade as soon as theirs have run.
        void loadModules(content.moduleUrls);
    }
    /**
     * The phone layout: the section's details give way to a header (title, link to the page and price) above the
     * variant picker and the buy buttons, beside the first picture.
     */
    #arrangeForPhone(grid, title, href) {
        const template = this.querySelector('template[data-quick-view-phone-header]');
        const header = template?.content.firstElementChild?.cloneNode(true);
        if (!(header instanceof HTMLElement))
            return;
        const price = grid.querySelector('product-price');
        const picker = grid.querySelector('variant-picker');
        const form = grid.querySelector('product-form-component');
        const heading = header.querySelector('[data-quick-view-heading]');
        if (heading)
            heading.textContent = title;
        const link = header.querySelector('a[data-quick-view-details]');
        if (link instanceof HTMLAnchorElement)
            link.href = href;
        if (price)
            header.append(price);
        grid.querySelector('.product-details')?.remove();
        grid.append(header);
        if (picker)
            grid.append(picker);
        if (form)
            grid.append(form);
    }
    /** Publishes the standard product view event for the section now in the dialog, once. */
    #dispatchView() {
        const root = this.#root;
        if (isViewEventElement(root) && root.hasAttribute('view-event-payload'))
            root.dispatchViewEvent();
    }
    /** Speaks through the dialog's polite live region. */
    #say(text) {
        const region = this.querySelector('[data-quick-view-status]');
        if (region)
            region.textContent = text ?? '';
    }
    /** Brings the dialog back to the state it was built in: nothing in it, the placeholder's heading holding the id. */
    #reset() {
        const root = this.#root;
        const loading = this.#loading;
        if (root && loading) {
            root.replaceChildren(loading);
            loading.hidden = true;
            loading.dataset.state = 'loading';
            root.removeAttribute('view-event-payload');
            root.removeAttribute('aria-busy');
        }
        const heading = this.#placeholderHeading;
        if (heading) {
            heading.id = TITLE_ID;
            heading.tabIndex = -1;
        }
        this.#say('');
    }
    /* ----- the shopper's actions inside the dialog ----- */
    /** A product added from the dialog closes it; the cart drawer takes over. A refusal leaves it open with its message. */
    #onCartLinesUpdate = (event) => {
        if (!hasPromise(event))
            return;
        event.promise
            .then((result) => {
            if (detailOf(result)?.didError === true)
                return;
            void this.closeDialog();
        })
            .catch((error) => {
            if (!isAbort(error))
                console.warn('[quick-view] The cart update did not settle:', error);
        });
    };
    /** A new variant moves the link to the page along with it. */
    #onProductSelect = (event) => {
        if (!hasPromise(event))
            return;
        event.promise
            .then((result) => {
            const html = detailOf(result)?.html;
            if (!(html instanceof Document))
                return;
            const linked = html.querySelector('a[data-quick-view-details]')?.getAttribute('href');
            const href = linked ? sameOriginHref(linked) : undefined;
            if (!href)
                return;
            for (const link of this.querySelectorAll('[data-quick-view-content] a[data-quick-view-details]'))
                link.href = href;
        })
            .catch((error) => {
            if (!isAbort(error))
                console.warn('[quick-view] The variant update did not settle:', error);
        });
    };
    #onBreakpoint = () => {
        void this.closeDialog();
    };
    /** Empties the dialog, so everything in it disconnects, and covers the iOS freeze below. */
    #onClosed = () => {
        this.#generation += 1;
        this.#reset();
        this.#restoreIOSLayout();
    };
    /**
     * An iOS before 16.4 froze the page when this dialog closed. Nudging the width of the grid behind it for a frame
     * makes the page paint again.
     */
    #restoreIOSLayout() {
        const version = getIOSVersion();
        if (!version || version.major >= 17 || (version.major === 16 && version.minor >= 4))
            return;
        requestAnimationFrame(() => {
            const grid = document.querySelector('#ResultsList [product-grid-view]');
            if (!grid)
                return;
            const { width } = grid.getBoundingClientRect();
            grid.style.width = `${width - 1}px`;
            requestAnimationFrame(() => {
                grid.style.width = '';
            });
        });
    }
}
if (!customElements.get('quick-add-dialog')) {
    customElements.define('quick-add-dialog', QuickAddDialog);
}
